import { Localized, useLocalization } from "@fluent/react"
import EmailIcon from "@mui/icons-material/Email"
import { LoadingButton } from "@mui/lab"
import { Alert, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup } from "@mui/material"
import { useEffect, useState } from "react"
import { unwrapResult } from "@reduxjs/toolkit"
import { APIError } from "../../../../app/appTypes"
import { useAppDispatch, useAppSelector } from "../../../../app/hooks"
import { selectSettings } from "../../../settings/settingsSlice"
import { showSuccess } from "../../../notifications/notificationsSlice"
import { ProjectProcessHeader, loadForProject } from "../../projectProcess/ProjectProcessApiClient"
import EmailInput from "../components/EmailInput"
import { MeetingMinute, ReportRequestParameters, closeMeeting, loadMeetingMinutes } from "../meetingMinutesSlice"

type CloseMeetingDialogProps = {
    meetingMinutesId: string
    meetingMinute?: MeetingMinute
    processes?: ProjectProcessHeader[]
    onClose: () => void
    onClosed: (meetingMinute: MeetingMinute) => void
    beforeConfirm?: () => Promise<boolean>
}

const defaultReportParameters: ReportRequestParameters = {
    reportType: "MEETING",
    reportId: null,
    reportSource: "MeetingMinutes",
    language: "pl",
    isDynamicReport: false,
    onlyCurrentMeeting: true,
    includeFullItemsHistory: true,
    selectedStatuses: [],
    selectedPriorities: [],
    selectedProcesses: [],
}

export const CloseMeetingDialog = ({
    meetingMinutesId, meetingMinute, processes, onClose, onClosed, beforeConfirm,
}: CloseMeetingDialogProps) => {
    const dispatch = useAppDispatch()
    const { locale } = useAppSelector(selectSettings)
    const { l10n } = useLocalization()
    const [loadedMinute, setLoadedMinute] = useState<MeetingMinute>()
    const [loadedProcesses, setLoadedProcesses] = useState<ProjectProcessHeader[]>()
    const [loading, setLoading] = useState(!meetingMinute)
    const [loadingProcesses, setLoadingProcesses] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<APIError | null>(null)
    const [sendGeneralReport, setSendGeneralReport] = useState(false)
    const [listOfRecipients, setListOfRecipients] = useState<string[]>([])
    const [attachNoteAttachments, setAttachNoteAttachments] = useState(true)
    const [reportRequestParameters, setReportRequestParameters] = useState(defaultReportParameters)
    const minute = meetingMinute ?? loadedMinute
    const operationalDashboardReport = minute?.parentObjectType === "OPERATIONAL_DASHBOARD"
    const needsProcesses = !operationalDashboardReport && !!minute?.parentObjectGuid

    useEffect(() => {
        if (meetingMinute) return
        let active = true
        dispatch(loadMeetingMinutes(meetingMinutesId))
            .then(unwrapResult)
            .then(result => { if (active) setLoadedMinute(result) })
            .catch((reason: APIError) => { if (active) setError(reason) })
            .finally(() => { if (active) setLoading(false) })
        return () => { active = false }
    }, [dispatch, meetingMinute, meetingMinutesId])

    useEffect(() => {
        if (!sendGeneralReport || processes || loadedProcesses || !needsProcesses) return
        let active = true
        setLoadingProcesses(true)
        dispatch(loadForProject(minute.parentObjectGuid))
            .then(unwrapResult)
            .then(result => { if (active) setLoadedProcesses(result) })
            .catch((reason: APIError) => { if (active) setError(reason) })
            .finally(() => { if (active) setLoadingProcesses(false) })
        return () => { active = false }
    }, [dispatch, sendGeneralReport, processes, loadedProcesses, needsProcesses, minute?.parentObjectGuid])

    const confirm = async () => {
        if (!minute || minute.status !== "INMEETING" || saving ||
            (sendGeneralReport && (loadingProcesses || needsProcesses && !processes && !loadedProcesses))) return
        setSaving(true)
        setError(null)
        try {
            if (beforeConfirm && !(await beforeConfirm())) return
            const result = await dispatch(closeMeeting({
                meetingMinuteId: minute.id,
                sendGeneralReport,
                listOfRecipients,
                attachNoteAttachments,
                language: locale,
                reportRequestParameters,
            }))
            if (closeMeeting.fulfilled.match(result)) {
                dispatch(showSuccess("meeting-minutes-closed-meeting"))
                onClosed(result.payload)
            } else {
                setError(result.payload ?? { kind: "unknown" })
            }
        } catch (reason) {
            console.error(reason)
            setError({ kind: "unknown" })
        } finally {
            setSaving(false)
        }
    }

    const errorMessage = error?.kind === "http" && error.problem?.title
        ? l10n.getString(error.problem.title, null, error.problem.detail ? `_${error.problem.detail}` : "_Nie udało się zakończyć spotkania.")
        : l10n.getString("error-unknown", null, "_Nie udało się zakończyć spotkania. Spróbuj ponownie.")

    return (
        <Dialog open fullWidth maxWidth={sendGeneralReport ? "md" : "sm"}>
            <DialogTitle sx={{ bgcolor: "action.hover" }}>
                <Localized id="meeting-minutes-close-meeting-question">_Czy zakończyć spotkanie?</Localized>
            </DialogTitle>
            <DialogContent>
                {loading && <CircularProgress sx={{ mt: 2 }} />}
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 2 }}>{errorMessage}</Alert>}
                {!loading && minute?.status !== "INMEETING" && !error && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        <Localized id="operational-dashboard-meeting-not-active">_Spotkanie nie jest już w toku. Odśwież dane.</Localized>
                    </Alert>
                )}
                {minute?.status === "INMEETING" && (
                    <FormGroup sx={{ mt: 1 }}>
                        <FormControlLabel control={<Checkbox checked={sendGeneralReport}
                            onChange={event => {
                                setSendGeneralReport(event.target.checked)
                                if (event.target.checked) {
                                    setReportRequestParameters({
                                        ...defaultReportParameters,
                                        reportType: operationalDashboardReport ? "OPERATIONAL_REPORT" : "MEETING",
                                    })
                                }
                            }} />}
                            label={<Localized id="meeting-minutes-send-report-meeting">_Wyślij raport ze spotkania</Localized>} />
                        {!sendGeneralReport && (
                            <Alert severity="info" sx={{ mt: 1 }}>
                                <Localized id="meeting-minutes-close-without-report">
                                    _Spotkanie zostanie zakończone bez wysyłania raportu. Zaznacz opcję powyżej, aby wysłać raport.
                                </Localized>
                            </Alert>
                        )}
                        {sendGeneralReport && (!needsProcesses || processes || loadedProcesses) && (
                            <EmailInput
                                meetingMinute={minute}
                                processes={operationalDashboardReport ? [] : processes ?? loadedProcesses ?? []}
                                defaultAttachNoteAttachments
                                onListOfRecipientsChange={setListOfRecipients}
                                onAttachNoteAttachmentsChanged={setAttachNoteAttachments}
                                onReportRequestParametersChange={setReportRequestParameters}
                                operationalDashboardReport={operationalDashboardReport} />
                        )}
                        {loadingProcesses && <CircularProgress sx={{ alignSelf: "center" }} />}
                    </FormGroup>
                )}
            </DialogContent>
            <DialogActions>
                <LoadingButton loading={saving} disabled={loading || loadingProcesses || minute?.status !== "INMEETING" || !!error ||
                    sendGeneralReport && needsProcesses && !processes && !loadedProcesses}
                    size="small" color="success" variant="contained"
                    startIcon={sendGeneralReport ? <EmailIcon /> : undefined} onClick={confirm}>
                    <Localized id="close">_Zakończ</Localized>
                </LoadingButton>
                <LoadingButton loading={saving} variant="outlined" size="small" onClick={onClose}>
                    <Localized id="cancel">_Anuluj</Localized>
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}
